import { warn } from 'app/services/logger';
import { getWindowOrThrow, getWindow } from 'app/utils/env';
import { isUrlOffAppSite } from 'app/utils/isUrlOffAppSite';
import {
  getDerivedUrlParts,
  getFullUrl,
  IParsedUrlParts,
  IPartialLocationObject,
  parseUrlLoose,
} from 'app/utils/LooseURL';
import { EventEmitter } from 'events';
import {
  createBrowserHistory,
  createMemoryHistory,
  History as BaseHistory,
  LocationDescriptor,
  LocationDescriptorObject,
} from 'history';
import QueryString from 'query-string';
import { notify } from 'app/services/errorReporter';

// Re-export here so that other parts of the app don't touch history
// npm package directly
export { LocationDescriptor, LocationDescriptorObject };

// eslint-disable-next-line no-alert
const defaultGetUserConfirmation = (message: any, callback: (b: boolean) => void) => {
  // eslint-disable-next-line no-alert
  callback(getWindowOrThrow().confirm(message));
};

const HISTORY_CHANGE_EVENT = 'history/change';

type UseBrowserHistory = boolean;
interface INavigationInfo {
  isOffsite: boolean;
  location: LocationDescriptor;
}
export type NavigationBlockerType = (props: INavigationInfo) => string | false;

interface IConstructorArgs {
  initialUrl?: string;
  getUserConfirmation?: (message: any, callback: (b: boolean) => void) => void;
  useBrowserHistory?: UseBrowserHistory;
  // Dependency injection for tests
  navigationMethods?: Partial<INavigationMethods>;
  addBeforeUnloadEventListener?: (callback: (e: BeforeUnloadEvent) => void) => void;
}

interface INavigationMethods {
  reload: () => void;
  assign: (href: string) => void;
  replace: (href: string) => void;
}

type DefaultState = unknown;

export interface IHistoryLocation<State = DefaultState> {
  href: string;
  hostname: string;
  port: string;
  host: string;
  protocol: string;
  origin: string;
  username: string;
  password: string;
  pathname: string;
  search: string;
  hash: string;
  state?: State;
  key: string | undefined;
}

export class History {
  private baseHistory: BaseHistory<DefaultState>;
  private emitter: EventEmitter;
  private wantsReloadOnNavigate: boolean;
  private deferrers: number;
  private navigationBlockers: NavigationBlockerType[];
  private fakeLocation?: IParsedUrlParts;
  private unlistenBaseHistory: () => void;
  private useBrowserHistory: UseBrowserHistory;
  private getUserConfirmation: (message: any, callback: (b: boolean) => void) => void;
  private offsiteNavigationConfirmed: boolean;
  private preservedParams: Set<string> = new Set();
  private navigationMethods: INavigationMethods;

  public constructor({
    initialUrl = getWindow()?.document.URL ?? '',
    getUserConfirmation = defaultGetUserConfirmation,
    useBrowserHistory = false,
    navigationMethods,
    addBeforeUnloadEventListener = (
      useBrowserHistory ?
        (callback) => getWindowOrThrow().addEventListener('beforeunload', callback) :
        () => {}
    ),
  }: IConstructorArgs = {}) {
    this.offsiteNavigationConfirmed = false;
    this.getUserConfirmation = getUserConfirmation;
    this.useBrowserHistory = useBrowserHistory;
    this.wantsReloadOnNavigate = false;
    this.deferrers = 0;
    this.navigationBlockers = [];

    this.navigationMethods = this.useBrowserHistory ? {
      reload: () => getWindowOrThrow().location.reload(),
      assign: (href: string) => getWindowOrThrow().location.assign(href),
      replace: (href: string) => getWindowOrThrow().location.replace(href),
      ...navigationMethods,
    } : {
      reload: () => warn('Ignoring window.location.reload()'),
      assign: (href) => warn('Ignoring window.location.assign() to:', href),
      replace: (href) => warn('Ignoring window.location.replace() to:', href),
      ...navigationMethods,
    };

    if (this.useBrowserHistory) {
      this.baseHistory = createBrowserHistory({ getUserConfirmation });
    } else {
      this.fakeLocation = parseUrlLoose(initialUrl);
      const { pathname, search, hash } = this.fakeLocation;

      this.baseHistory = createMemoryHistory({
        getUserConfirmation,
        initialEntries: [`${pathname}${search}${hash}`],
        initialIndex: 0,
      });
    }

    // Set up block event listeners for
    addBeforeUnloadEventListener((e: BeforeUnloadEvent) => {
      // If the warnings have already been confirmed, don't re-prompt
      if (this.offsiteNavigationConfirmed) {
        // Make sure to always reset the state to false to be safe.
        this.offsiteNavigationConfirmed = false;
        return;
      }

      const warnings = this.aggregateBlockWarnings({
        isOffsite: true, // In beforeunload we have to assume it's offsite.
        location: {},
      });
      if (warnings) {
        e.preventDefault();
        // eslint-disable-next-line no-param-reassign
        e.returnValue = warnings;
      }
    });

    if (this.useBrowserHistory) {
      getWindowOrThrow().addEventListener('pagehide', (e) => {
        if (e.persisted === false) {
          // Make sure to always reset the state to false to be safe.
          this.offsiteNavigationConfirmed = false;
        }
      });
    }

    this.baseHistory.block((location, action) => {
      const warnings = this.aggregateBlockWarnings({
        // Is offsite should be calculated since the basePath might be different.
        isOffsite: isUrlOffAppSite(this.createHref(location)),
        location,
      });
      return warnings || undefined;
    });

    this.unlistenBaseHistory = this.baseHistory.listen((location, action) => {
      if (this.fakeLocation) {
        // Apply updates to the internal state from the event
        this.fakeLocation.pathname = location.pathname;
        this.fakeLocation.search   = location.search;
        this.fakeLocation.hash     = location.hash;
      }

      // Pass our custom location instead of the base history object's location to event.
      this.emitter.emit(HISTORY_CHANGE_EVENT, this.location, action);
    });

    this.emitter = new EventEmitter();
    this.emitter.setMaxListeners(Infinity);
  }

  public get isServerRendering(): boolean {
    return !this.useBrowserHistory;
  }

  // Encapsulate access to base history object.
  public get location(): IHistoryLocation {
    const { pathname, search, hash, state, key } = this.baseHistory.location;
    // Convert null to undefined here due to poor typings of the history package
    const historyProps = { pathname, search, hash, state: state ?? undefined, key };
    const windowLocation = getWindow()?.location;

    if (this.useBrowserHistory && windowLocation !== undefined) {
      const { hostname, port, host, protocol, origin, href } = windowLocation;
      // window.location does not include username/password, so just use empty strings.
      return { hostname, port, host, protocol, origin, href, username: '', password: '', ...historyProps };
    } else if (this.fakeLocation !== undefined) {
      const { hostname, port, protocol, username, password } = this.fakeLocation;
      const { host, origin, href } = getDerivedUrlParts(this.fakeLocation);
      return { hostname, port, host, protocol, origin, href, username, password, ...historyProps };
    } else {
      throw new Error('Trying to use browser history without location object.');
    }
  }

  public get length() {
    return this.baseHistory.length;
  }

  public go = (delta: number) => {
    return this.baseHistory.go(delta);
  };

  public goBack = () => {
    return this.baseHistory.go(-1);
  };

  public goForward = () => {
    return this.baseHistory.go(1);
  };

  public push = (locationOrPath: LocationDescriptor) => {
    return this.pushOrReplace('push', locationOrPath);
  };

  public replace = (locationOrPath: LocationDescriptor) => {
    return this.pushOrReplace('replace', locationOrPath);
  };

  public createHref = (location: LocationDescriptorObject) => {
    return this.baseHistory.createHref(location);
  };

  /**
   * Extends current location (omitting preserved params). Tries
   * to mimmick how browser url resolution works.
   *
   * 1. If nothing is set, returns href for current location
   * 1. If only `hash` is set, `search` and `pathname` is inherited
   * 2. If `search` is set, only `pathname` is inherited
   * 3. If `pathname` is set, nothing is inherited
   */
  public extendHref = ({ pathname, search, hash }: LocationDescriptorObject): string => {
    if (pathname !== undefined) {
      return this.createHref({ pathname, search, hash });
    } else if (search !== undefined) {
      return this.createHref({
        pathname: this.location.pathname,
        search,
        hash,
      });
    } else {
      const current = this.location;
      const query = QueryString.parse(current.search);

      for (const param of this.preservedParams) {
        delete query[param];
      }

      return this.createHref({
        pathname: current.pathname,
        search: QueryString.stringify(query),
        hash: hash !== undefined ? hash : current.hash,
      });
    }
  };

  public listen = (callback: (location: IHistoryLocation, action: string) => any) => {
    this.emitter.on(HISTORY_CHANGE_EVENT, callback);

    const unlisten = () => {
      this.emitter.removeListener(HISTORY_CHANGE_EVENT, callback);
    };

    return unlisten;
  };

  /**
   * Add a navigation blocker to this history's list of potential on or off site blockers
   *
   * @param newBlocker - The NavigationBlocker to add to our list of blockers
   * @returns removeNavigationBlocker - A callback to remove the blocker from the list.
   */
  public addNavigationBlocker = (newBlocker: NavigationBlockerType) => {
    // Blockers added later will be considered first, and their message will be listed higher
    // in warning message. We are using #unshift to put place blockers rendered deeper in
    // the tree in a position of higher authority. The hope is that more specific messages will
    // appear above the more general site wide ones.
    this.navigationBlockers.unshift(newBlocker);
    const removeNavigationBlocker = () => {
      this.navigationBlockers = this.navigationBlockers.filter(blocker => blocker !== newBlocker);
    };
    return removeNavigationBlocker;
  };

  /**
   * Will cause the history service to ignore any blocking logic in the next navigation that
   * will trigger beforeunload.
   *
   * In practice it is implemented by automatically confirming the navigation. For this reason,
   * it should only be used if there is a very good reason to force a confirmation.
   */
  public dangerouslyConfirmNextBrowserNavigation = () => {
    this.offsiteNavigationConfirmed = true;
  };

  /**
   * Resolve relative URLs relative to this history object's current location.
   */
  public getAbsoluteUrl = (relativeUrl: string) => {
    return getFullUrl(parseUrlLoose(relativeUrl, this.location));
  };

  public setWantsReloadOnNavigate = (wantsReloadOnNavigate: boolean) => {
    this.wantsReloadOnNavigate = wantsReloadOnNavigate;
  };

  public deferReloadOnNavigate = () => {
    this.deferrers += 1;
    let released = false;
    return () => {
      if (!released) {
        released = true;
        this.deferrers = Math.max(0, this.deferrers - 1);
      }
    };
  };

  public reload = () => {
    this.navigationMethods.reload();
  };

  /**
   * Send the user to the url given.
   * Caution: May send the user offsite
   */
  public hardPush = (url: string) => {
    this.hardPushOrReplace('push', url);
  };

  /**
   * Send the user to the url given and replace the browser history.
   * Caution: May send the user offsite
   */
  public hardReplace = (url: string) => {
    this.hardPushOrReplace('replace', url);
  };

  /**
   * Based on the url, redirect the user and preserve their history.
   *
   * This method will potentially redirect the user offsite. It is important to
   * validate any offsite urls before calling.
   */
  public smartPush = (locationOrPath: LocationDescriptor | IPartialLocationObject) => {
    warn(`HistoryService: smartPush to ${JSON.stringify(locationOrPath)}`);
    this.smartPushOrReplace('push', locationOrPath);
  };

  /**
   * Based on the url, redirect the user and do not preserve their history.
   *
   * This method will potentially redirect the user offsite. It is important to
   * validate any offsite urls before calling.
   */
  public smartReplace = (locationOrPath: LocationDescriptor | IPartialLocationObject) => {
    warn(`HistoryService: smartReplace to ${JSON.stringify(locationOrPath)}`);
    this.smartPushOrReplace('replace', locationOrPath);
  };

  // This technically should be private within the module, to be called by setGlobalHistory.
  // Do not call this method, elsewhere.
  public cleanup = () => {
    this.unlistenBaseHistory();

    if (this.emitter.listenerCount(HISTORY_CHANGE_EVENT) > 0) {
      notify(new Error('Potential memory leak: listeners still attached to history object.'), {
        context: {
          listenerCount: this.emitter.listenerCount(HISTORY_CHANGE_EVENT),
        },
      });
    }
  };

  /**
   * Register query parameter names to be preserved between
   * navigations. These params will be carried over between
   * navigation events.
   *
   * Throws if a param is already registered. Returns a function
   * to unregister provided `params`. When calling the unregister
   * function, the previosly-preserved params are removed from URL
   * through a `replace` method
   */
  public preserveParams = (...params: string[]) => {
    const { preservedParams } = this;

    for (const param of params) {
      if (preservedParams.has(param)) {
        throw new Error(`Param ${param} is already registered for preservation`);
      }
    }

    for (const param of params) {
      preservedParams.add(param);
    }

    return () => {
      for (const param of params) {
        preservedParams.delete(param);
      }
    };
  };

  // Private methods

  /**
   * Check a potential navigation with each navigation blocker and return an aggregation
   * of their messages.
   *
   * @param info the parameters of the navigation for blockers to use to decide to block.
   * @returns aggregateMessage a single string containing all blocking warning messages
   * or false if no messages were generated.
   *
   * The output matches the interface that baseHistory.block is expecting.
   */
  private aggregateBlockWarnings = ({
    isOffsite,
    location,
  }: INavigationInfo) => {
    const warnings: string[] = [];
    // Get a copy of the blockers before iterating to avoid worrying about blockers being
    // added or removed during the iteration.
    const blockersSnapshot = this.navigationBlockers.slice();
    for (const blocker of blockersSnapshot) {
      const warning = blocker({ isOffsite, location });
      if (warning) {
        warnings.push(warning);
      }
    }

    return warnings.length === 0 ? false : warnings.join('\n\n');
  };

  private pushOrReplace(method: 'push' | 'replace', locationOrPath: LocationDescriptor) {
    const href: string = this.convertLocationDescriptorToRelativeUrl(locationOrPath);
    const state = extractStateFromDescriptor(locationOrPath);

    warn(`HistoryService: ${method} to ${href}`, state);

    if (this.wantsReloadOnNavigate && this.deferrers === 0) {
      this.hardPush(href);
    } else {
      this.baseHistory[method](href, state as any);
    }
  }

  private hardPushOrReplace = (method: 'push' | 'replace', url: string) => {
    const objectUrl = parseUrlLoose(url);
    const href = getFullUrl({
      ...objectUrl,
      search: this.mergeWithPreservedQueryParams(objectUrl.search),
    });

    const warnings = this.aggregateBlockWarnings({
      // the URL might be on-site even if the user is hard pushing (see wantsReloadOnNavigate behavior)
      isOffsite: isUrlOffAppSite(href),
      location: href,
    });
    if (warnings) {
      this.getUserConfirmation(warnings, (confirmed => {
        this.offsiteNavigationConfirmed = confirmed;
      }));

      // Cancel if the user did not confirm the navigation.
      if (!this.offsiteNavigationConfirmed) {
        return;
      }
    } else {
      // Tell beforeunload handler to not ask for confirmation, because we already know
      // confirmation isn't needed.
      this.dangerouslyConfirmNextBrowserNavigation();
    }

    if (method === 'push') {
      warn(`HistoryService: hardPush to ${href}`);
      this.navigationMethods.assign(href);
    } else {
      warn(`HistoryService: hardReplace to ${href}`);
      this.navigationMethods.replace(href);
    }
  };

  private smartPushOrReplace = (
    method: 'push' | 'replace',
    locationOrPath: LocationDescriptor | IPartialLocationObject,
  ) => {
    const url = typeof locationOrPath === 'string' ?
      locationOrPath :
      getFullUrl(locationOrPath);

    if (isUrlOffAppSite(url)) {
      this.hardPushOrReplace(method, url);
    } else {
      this.pushOrReplace(method, locationOrPath);
    }
  };

  /**
   * Takes a location descriptor and converts it into an href string localized to the replace site.
   * @param locationOrPath the location descriptor to convert
   * @returns href for the path, search, and hash with preservedParams on the replace app site.
   */
  private convertLocationDescriptorToRelativeUrl(locationOrPath: LocationDescriptor) {
    const { pathname, search, hash } =
      typeof locationOrPath === 'string'
      ? parseUrlLoose(locationOrPath)
      : locationOrPath;

    return this.baseHistory.createHref({
      pathname,
      search: this.mergeWithPreservedQueryParams(search),
      hash,
    });
  }

  private mergeWithPreservedQueryParams(search?: string): string {
    const oldQuery = QueryString.parse(this.location.search);
    const newQuery = QueryString.parse(search || '');

    for (const param of this.preservedParams) {
      if (param in oldQuery && !(param in newQuery)) {
        newQuery[param] = oldQuery[param];
      }
    }

    return QueryString.stringify(newQuery) || '';
  }
}

const extractStateFromDescriptor = (locationOrPath: LocationDescriptor): unknown =>
  (typeof locationOrPath === 'string' ? undefined : locationOrPath.state);

export const createHistory = (options: IConstructorArgs) => new History(options);

let globalHistory: History | undefined;

export const setGlobalHistory = (history: History | undefined) => {
  if (globalHistory) {
    globalHistory.cleanup();
  }

  globalHistory = history;
};

export const getGlobalHistory = (): History => {
  if (!globalHistory) {
    throw new Error('Global history not set');
  }

  return globalHistory;
};
